import { useRef, useState, useEffect } from 'react';

interface Service {
  id: number;
  name: string;
  description: string;
  image: string;
  icon: string;
}

export default function Capabilities() {
  const [activeService, setActiveService] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  const services: Service[] = [
    {
      id: 1,
      name: 'Construction Finishing',
      description: 'From structural completion to refined surfaces. We transform raw spaces into finished environments where every detail reflects precision and quality.',
      image: 'https://readdy.ai/api/search-image?query=skilled%20Black%20African%20craftsman%20applying%20premium%20wall%20finish%20in%20luxury%20interior%2C%20Nigerian%20construction%20worker%20doing%20detailed%20finishing%20work%2C%20warm%20lighting%2C%20professional%20interior%20construction%2C%20high%20end%20residential%20project%2C%20editorial%20photography%20style&width=800&height=600&seq=cap001c&orientation=landscape',
      icon: 'ri-building-2-line'
    },
    {
      id: 2,
      name: 'Interior Furnishing',
      description: 'Curated selections and bespoke pieces that complete spaces. We source and specify furnishings that align with architectural intent and client vision.',
      image: 'https://readdy.ai/api/search-image?query=Black%20African%20interior%20designer%20arranging%20premium%20furniture%20in%20elegant%20living%20room%2C%20Nigerian%20professional%20styling%20luxury%20residential%20space%2C%20sophisticated%20design%2C%20warm%20ambient%20lighting%2C%20high%20end%20residential%20styling%2C%20editorial%20photography&width=800&height=600&seq=cap002c&orientation=landscape',
      icon: 'ri-sofa-line'
    },
    {
      id: 3,
      name: 'Bespoke Detailing',
      description: 'Custom millwork, fixtures, and architectural elements designed specifically for your space. Where standard solutions fall short, we create.',
      image: 'https://readdy.ai/api/search-image?query=skilled%20Black%20African%20carpenter%20working%20on%20bespoke%20wood%20paneling%2C%20Nigerian%20craftsman%20doing%20precision%20millwork%20installation%2C%20custom%20woodwork%20detail%2C%20luxury%20interior%20construction%2C%20warm%20workshop%20lighting%2C%20editorial%20photography&width=800&height=600&seq=cap003c&orientation=landscape',
      icon: 'ri-tools-line'
    },
    {
      id: 4,
      name: 'Project Execution',
      description: 'End-to-end management from concept to completion. We coordinate trades, manage timelines, and ensure every phase meets our exacting standards.',
      image: 'https://readdy.ai/api/search-image?query=Black%20African%20construction%20project%20manager%20reviewing%20architectural%20plans%20with%20team%20on%20site%2C%20Nigerian%20professionals%20in%20modern%20luxury%20building%20interior%2C%20collaborative%20work%20environment%2C%20diverse%20team%20of%20Black%20men%2C%20editorial%20photography%20style&width=800&height=600&seq=cap004c&orientation=landscape',
      icon: 'ri-compass-3-line'
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="w-full bg-[#F8F6F3] py-16 md:py-24 lg:py-32 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-8 lg:px-16">
        {/* Header */}
        <div className={`text-center mb-10 md:mb-16 lg:mb-20 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="flex items-center justify-center gap-3 md:gap-4 mb-4 md:mb-6">
            <div className="w-8 md:w-12 h-px bg-[#C9A86C]" />
            <span className="text-[#C9A86C] text-xs md:text-sm tracking-[0.2em] md:tracking-[0.3em] uppercase">Services</span>
            <div className="w-8 md:w-12 h-px bg-[#C9A86C]" />
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif text-[#0A0A0A] mb-4 md:mb-6">
            What We <span className="text-[#C9A86C]">Do</span>
          </h2>
          <p className="text-[#6B6B6B] max-w-xl mx-auto text-sm md:text-base">
            Services delivered as experiences, not transactions. Each engagement is tailored to your unique vision.
          </p>
        </div>

        {/* Services Display */}
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-center transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {/* Image Side */}
          <div className="relative h-[300px] sm:h-[400px] md:h-[500px] lg:h-[600px] order-2 lg:order-1">
            {services.map((service, index) => (
              <div
                key={service.id}
                className={`absolute inset-0 transition-all duration-700 ${
                  activeService === index ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
                }`}
              >
                <img
                  src={service.image}
                  alt={service.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/30 to-transparent" />
              </div>
            ))}
            
            {/* Decorative Frame */}
            <div className="absolute -bottom-2 -right-2 md:-bottom-4 md:-right-4 w-full h-full border-2 border-[#C9A86C] -z-10" />
            <div className="absolute -top-2 -left-2 md:-top-4 md:-left-4 w-12 md:w-24 h-12 md:h-24 bg-[#C9A86C]" />
          </div>

          {/* Services List */}
          <div className="space-y-3 md:space-y-4 order-1 lg:order-2">
            {services.map((service, index) => (
              <div
                key={service.id}
                onClick={() => setActiveService(index)}
                className={`group p-4 md:p-6 border transition-all duration-500 cursor-pointer ${
                  activeService === index
                    ? 'bg-[#0A0A0A] border-[#0A0A0A]'
                    : 'bg-white border-[#E5E5E5] hover:border-[#C9A86C]'
                }`}
              >
                <div className="flex items-start gap-3 md:gap-5">
                  <div className={`w-10 h-10 md:w-14 md:h-14 flex-shrink-0 flex items-center justify-center transition-colors duration-500 ${
                    activeService === index ? 'bg-[#C9A86C]' : 'bg-[#F8F6F3] group-hover:bg-[#C9A86C]/20'
                  }`}>
                    <i className={`${service.icon} text-xl md:text-2xl ${activeService === index ? 'text-[#0A0A0A]' : 'text-[#C9A86C]'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-base md:text-xl font-serif mb-1 md:mb-2 transition-colors duration-500 ${
                      activeService === index ? 'text-white' : 'text-[#0A0A0A]'
                    }`}>
                      {service.name}
                    </h3>
                    <p className={`text-xs md:text-sm leading-relaxed transition-all duration-500 ${
                      activeService === index ? 'text-white/70 max-h-20 opacity-100' : 'text-[#6B6B6B] max-h-0 opacity-0 overflow-hidden'
                    }`}>
                      {service.description}
                    </p>
                  </div>
                  <div className={`w-6 h-6 md:w-8 md:h-8 flex-shrink-0 flex items-center justify-center transition-all duration-500 ${
                    activeService === index ? 'rotate-45' : ''
                  }`}>
                    <i className={`ri-add-line text-lg md:text-xl ${activeService === index ? 'text-[#C9A86C]' : 'text-[#0A0A0A]'}`} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
